import React, { useState } from "react";
import axios from "axios";
import "./UpdateDelivery.css";

const UpdateDeliveryBatch = () => {
  const [reportDate, setReportDate] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [refreshHint, setRefreshHint] = useState(false); // ✅ subtle refresh hint

  // === Fetch bookings ===
  const fetchBookings = async (clearMsg = true, showSuccessMsg = true) => {
    if (!reportDate) {
      setMessage({ text: "Please select a date", type: "error" });
      return;
    }

    setBookings([]);
    setLoading(true);
    if (clearMsg) setMessage({ text: "", type: "" });

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/get_bookings`,
        { date: reportDate }
      );

      if (res.data.status === "success") {
        const cleanData = res.data.data.map((b) => ({
          ...b,
          Delivery_Status: b.Delivery_Status?.trim() || "",
          Actual_Delivery_Date: b.Actual_Delivery_Date || "",
          Remarks: b.Remarks || "", // ✅ include remarks
          _edited: false,
        }));
        setBookings(cleanData);

        if (showSuccessMsg) {
          setMessage({ text: "Bookings loaded successfully", type: "success" });
        } else {
          setRefreshHint(true);
          setTimeout(() => setRefreshHint(false), 2000);
        }
      } else {
        setMessage({
          text: res.data.message || "No data found for this date",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Error fetching data", type: "error" });
    } finally {
      setLoading(false);
      if (clearMsg || showSuccessMsg)
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    }
  };

  // === Handle field change ===
  const handleChange = (index, field, value) => {
    const updated = [...bookings];
    updated[index][field] = value;
    updated[index]._edited = true;

    if (field === "Delivery_Status") {
      const today = new Date().toISOString().split("T")[0];
      if (value === "Cancelled" || value === "Delivered") {
        updated[index]["Actual_Delivery_Date"] = today;
      } else {
        updated[index]["Actual_Delivery_Date"] = "";
      }
    }

    setBookings(updated);
  };

  // === Save all edited bookings ===
  const handleSaveAll = async () => {
    const changed = bookings.filter((b) => b._edited);
    if (changed.length === 0) {
      setMessage({ text: "No changes to save", type: "info" });
      setTimeout(() => setMessage({ text: "", type: "" }), 2000);
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/update_bookings_batch`,
        { bookings: changed }
      );

      if (res.data.status === "success" || res.data.status === "partial") {
        setMessage({
          text: res.data.message,
          type: res.data.status === "success" ? "success" : "warning",
        });

        await new Promise((r) => setTimeout(r, 400));
        await fetchBookings(false, false);
      } else {
        setMessage({ text: res.data.message, type: "error" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Error saving updates", type: "error" });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    }
  };

  // === Helpers ===
  const getRowClass = (status) => {
    const s = status?.trim() || "";
    if (s === "Pending" || s === "") return "row-pending";
    if (s === "Cancelled") return "row-cancelled";
    if (s === "Delivered") return "row-delivered";
    return "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  return (
    <div className="container">
      <div className="admin-wrap">
        <div className="box-head" style={{ marginBottom: "5px" }}>
          <span>Update Deliveries In Batch</span>
        </div>

        <div className="controls">
          <p style={{ lineHeight: "10px" }}>Booking Date</p>
          <input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
          />
          <button
            onClick={() => fetchBookings(true)}
            disabled={loading || saving}
          >
            {loading ? "Loading..." : "Load Bookings"}
          </button>
          <button onClick={handleSaveAll} disabled={saving || loading}>
            {saving ? "Saving All..." : "Save All Changes"}
          </button>
        </div>

        {message.text && (
          <div
            className={`inline-message ${
              message.type === "success"
                ? "msg-success"
                : message.type === "error"
                ? "msg-error"
                : message.type === "warning"
                ? "msg-warning"
                : "msg-info"
            }`}
          >
            {message.text}
          </div>
        )}

        {refreshHint && <div className="refresh-hint">🔄 Refreshed</div>}

        {bookings.length > 0 && (
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Villa No</th>
                  <th>Booked On</th>
                  <th>Status</th>
                  <th>Delivery Date</th>
                  <th>Remarks</th> {/* ✅ new column */}
                </tr>
              </thead>
              <tbody>
                {bookings.map((b, i) => (
                  <tr
                    key={`${b.Villa_No}-${b.Booking_Day}-${
                      b.Booking_No || "N"
                    }`}
                    className={getRowClass(b.Delivery_Status)}
                  >
                    <td data-label="No">{b.Booking_No}</td>
                    <td data-label="Villa No">{b.Villa_No}</td>
                    <td data-label="Booked On">{formatDate(b.Booking_Day)}</td>
                    <td data-label="Status">
                      <select
                        value={b.Delivery_Status}
                        onChange={(e) =>
                          handleChange(i, "Delivery_Status", e.target.value)
                        }
                        className={`status-select ${getRowClass(
                          b.Delivery_Status
                        )}`}
                      >
                        <option value="">--Select--</option>
                        <option value="Pending">Pending</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td data-label="Delivery Date">
                      <input
                        type="date"
                        value={b.Actual_Delivery_Date || ""}
                        onChange={(e) =>
                          handleChange(
                            i,
                            "Actual_Delivery_Date",
                            e.target.value
                          )
                        }
                        disabled={
                          b.Delivery_Status === "Pending" || !b.Delivery_Status
                        }
                      />
                    </td>
                    <td data-label="Remarks">
                      <input
                        type="text"
                        value={b.Remarks || ""}
                        placeholder="Enter remarks"
                        onChange={(e) =>
                          handleChange(i, "Remarks", e.target.value)
                        }
                        maxLength={100}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(loading || saving) && (
          <div className="overlay">
            <div className="spinner"></div>
            <p>{saving ? "Saving changes..." : "Loading data..."}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateDeliveryBatch;
